import {IconButton, Link} from '@wandb/components';
import {debounce} from 'lodash';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {useIsInLoadedReportContext} from '../pages/ReportPage/LoadedReportContext';
import type {CriticalWorkflowInitiatingEvent} from '../util/datadog/criticalWorkflows/painWindow/types';
import {getCriticalWorkflowInitiatingEvent} from '../util/datadog/criticalWorkflows/useCriticalWorkflowTrigger';
import DOC_URLS from '../util/doc_urls';
import {SearchHistory} from './PanelBank/search/SearchHistory';
import {trackSearchHistory} from './PanelBank/search/searchHistoryEvents';
import {
  type OptionTypeWithRegex,
  type RunSearchHistoryItem,
} from './PanelBank/types';

interface RunsSearchBarProps {
  isGroupSearch: boolean;
  search: {
    query: string;
    isRegex: boolean;
  };
  setSearch(
    search: string,
    isRegex: boolean,
    initiatingEvent?: CriticalWorkflowInitiatingEvent
  ): void;
  searchHistory?: RunSearchHistoryItem[];
  onAddToHistory?(query: string, regex?: boolean): void;
  errorMessage?: string;
}

const SEARCH_DEBOUNCE_MS = 500;

const RunsSearchBarComp: React.FC<RunsSearchBarProps> = ({
  isGroupSearch,
  search: propsSearch,
  setSearch,
  searchHistory,
  onAddToHistory,
  errorMessage,
}) => {
  const isInReport = useIsInLoadedReportContext();
  const [inputValue, setInputValue] = useState(propsSearch.query);
  const [regex, setRegex] = useState(propsSearch.isRegex);

  const [selectedOption, setSelectedOption] =
    useState<OptionTypeWithRegex | null>(null);
  const latestSearchRef = useRef(propsSearch);
  const latestInitiatingEventRef = useRef<
    CriticalWorkflowInitiatingEvent | undefined
  >(undefined);
  const isInputFocusedRef = useRef(false);

  /**
   * Used for analytics tracking. We want to log "Runs searched" event
   * on blur, but only if the value has actually changed since focused.
   */
  const [valueAtFocus, setValueAtFocus] = useState('');

  const commitSearch = useCallback(
    (value: string, nextRegex: boolean) => {
      const latestSearch = latestSearchRef.current;
      const initiatingEvent = latestInitiatingEventRef.current;
      latestInitiatingEventRef.current = undefined;
      if (value !== latestSearch.query || nextRegex !== latestSearch.isRegex) {
        latestSearchRef.current = {query: value, isRegex: nextRegex};
        setSearch(value, nextRegex, initiatingEvent);
      }
    },
    [setSearch]
  );

  const debouncedSearch = useMemo(
    () => debounce(commitSearch, SEARCH_DEBOUNCE_MS),
    [commitSearch]
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  useEffect(() => {
    latestSearchRef.current = {
      query: propsSearch.query,
      isRegex: propsSearch.isRegex,
    };
    if (isInputFocusedRef.current) {
      debouncedSearch.cancel();
      return;
    }
    setInputValue(propsSearch.query);
    setRegex(propsSearch.isRegex);
  }, [debouncedSearch, propsSearch.isRegex, propsSearch.query]);

  useEffect(() => {
    const input = inputValue.trim();
    if (
      input === latestSearchRef.current.query &&
      regex === latestSearchRef.current.isRegex
    ) {
      debouncedSearch.cancel();
      return;
    }
    debouncedSearch(input, regex);
  }, [inputValue, debouncedSearch, regex]);

  const commitInputValue = useCallback(
    (value: string, nextRegex: boolean) => {
      debouncedSearch.cancel();
      commitSearch(value.trim(), nextRegex);
    },
    [commitSearch, debouncedSearch]
  );

  const toggleRegex = useCallback(
    (event: React.MouseEvent) => {
      latestInitiatingEventRef.current =
        getCriticalWorkflowInitiatingEvent(event);
      setRegex(prev => {
        const next = !prev;
        trackSearchHistory({
          action: 'Regex toggled',
          location: isInReport
            ? 'report run selector'
            : 'workspace run selector',
          regex: next ? 'on' : 'off',
        });
        return next;
      });
      event.stopPropagation();
    },
    [isInReport]
  );

  const recordInitiatingEvent = useCallback((event: React.SyntheticEvent) => {
    latestInitiatingEventRef.current =
      getCriticalWorkflowInitiatingEvent(event);
  }, []);

  const onFocus = () => {
    isInputFocusedRef.current = true;
    if (valueAtFocus !== inputValue) {
      setValueAtFocus(inputValue);
    }
  };

  const onBlur = (inputValue: string) => {
    isInputFocusedRef.current = false;
    commitInputValue(inputValue, regex);
    if (inputValue && inputValue !== valueAtFocus) {
      trackSearchHistory({
        action: 'Item added',
        location: isInReport ? 'report run selector' : 'workspace run selector',
        regex: regex ? 'on' : 'off',
      });
    }
    setValueAtFocus('');
    // when to add to search history is a bit tricky - we chose blur since
    // it is a reasonable proxy for "the user finished editing the search",
    // and trying to save after X seconds of pause could be wrong if a
    // user stopped to think of something while typing a search.
    if (!inputValue) {
      return;
    }
    onAddToHistory?.(inputValue, regex);
    trackSearchHistory({
      action: 'Item selected',
      location: isInReport ? 'report run selector' : 'workspace run selector',
      regex: regex ? 'on' : 'off',
    });
  };

  const onOptionChange = (newValue: OptionTypeWithRegex | string) => {
    const newOption =
      typeof newValue === 'string'
        ? {
            value: newValue,
            label: newValue,
            isRegex: regex,
          }
        : newValue;
    setSelectedOption(newOption);
    setRegex(newOption.isRegex);
  };

  const customActionButton = (
    <IconButton
      isActive={regex}
      aria-label="Toggle regex matching"
      onClick={toggleRegex}
      icon="regex"
      size="sm"
      variant="secondary"
      tooltip={{children: `Regex matching is ${regex ? 'on' : 'off'}`}}
    />
  );

  return (
    <SearchHistory
      searchHistory={searchHistory ?? []}
      onOptionChange={onOptionChange}
      isCollapsible
      selectedOption={selectedOption}
      setSelectedOption={setSelectedOption}
      onFocus={onFocus}
      onBlur={onBlur}
      minWidth={isInReport ? 200 : 150}
      setInputValue={setInputValue}
      inputValue={inputValue ?? ''}
      placeholderText={`Search ${isGroupSearch ? 'groups' : 'runs'}`}
      ddActionName={isGroupSearch ? 'Search groups' : 'Search runs'}
      onInteraction={recordInitiatingEvent}
      maxItems={10}
      customActionButton={customActionButton}
      error={Boolean(errorMessage)}
      errorMessage={
        errorMessage != null ? (
          <>
            {errorMessage}{' '}
            <Link
              href={DOC_URLS.searchRuns}
              target="_blank"
              rel="noopener noreferrer">
              See supported syntax
            </Link>
          </>
        ) : undefined
      }
    />
  );
};

export const RunsSearchBar = memo(RunsSearchBarComp);
